import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  NewProjectFormData,
  NewProjectImage,
  NewSubProject
} from '../../types/newProject';
import { newProjectService } from '../../services/newProjectService.ts';
import { newValidationService } from '../../services/newValidationService.ts';
import { 
  resetSubProjects,
  clearPostulationProject,
  openMainModal,
  closeMainModal,
  getPrivateProjectById,
  openEdifySpinner,
  getProjectsByType
} from '../../store/actions';
import { fireAnalytics } from '../../utils/firebase';
import { getSessionId, getDeviceCategory } from '../../utils/analyticsHelpers.ts';

export const useNewProjectCreation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.general.userData);

  // Validar datos del proyecto para creación
  const validateProjectForCreation = useCallback((projectData: NewProjectFormData): boolean => {
    return newValidationService.validateProjectData(projectData);
  }, []);

  // Validar subproyectos para creación
  const validateSubProjectsForCreation = useCallback((subProjects: NewSubProject[]): boolean => {
    if (subProjects.length === 0) return false;
    return subProjects.every(sub => newValidationService.validateSubProjectData(sub));
  }, []);

  // Validar imágenes para creación
  const validateImagesForCreation = useCallback((images: NewProjectImage[]): boolean => {
    return images.length > 0 && images.every(img => img.file !== null);
  }, []);

  // Crear proyecto con subproyectos
  const createProjectWithSubProjects = useCallback(async (
    projectData: NewProjectFormData,
    subProjects: NewSubProject[],
    images: NewProjectImage[]
  ): Promise<void> => {
    // Validar datos antes de crear
    if (!validateProjectForCreation(projectData)) {
      throw new Error('Los datos del proyecto no son válidos');
    }

    if (!validateSubProjectsForCreation(subProjects)) {
      throw new Error('Los subproyectos no son válidos');
    }

    if (!validateImagesForCreation(images)) {
      throw new Error('Las imágenes no son válidas');
    }

    try {
      const createdProject = await newProjectService.createProject(projectData, images);
      const subProjectPromises = subProjects.map(subProject =>
        newProjectService.createSubProject(subProject, createdProject.id, projectData.startDate, projectData.endDate)
      );
      await Promise.all(subProjectPromises);

      dispatch(resetSubProjects());
      dispatch(clearPostulationProject());

      fireAnalytics("project_created", {
        edify_user_id: userData?.id ?? "",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "publishing",
        action: "project_created",
        project_type: "PRIVATE",
        project_id: createdProject.id ?? ""
      });

      dispatch(openMainModal({
        title: " ¡Gracias por publicar en edify! ",
        iconType: "success",
        body: "El proyecto ya se encuentra publicado en la plataforma, ¡esperamos que tengas muchos presupuestos!",
        isSuccess: true,
        noLabel: "Cerrar",
        customCloseMainModal: () => {
          dispatch(closeMainModal());
          (dispatch as any)(getPrivateProjectById(createdProject.id));
          navigate(`/obrasprivadas/${createdProject.id}`);
        }
      }));
    } catch (error) {
      console.error('Error al crear proyecto:', error);
      throw error;
    }
  }, [validateProjectForCreation, validateSubProjectsForCreation, validateImagesForCreation, dispatch, navigate, userData?.id]);

  const deleteProjectWithFlow = useCallback((projectId: string | number): void => {
    const id = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;
    dispatch(openMainModal({
      title: "¿Estás seguro que deseas eliminar el proyecto?",
      body: "Una vez eliminado no podrás recuperar los datos ingresados",
      iconType: "warning",
      confirmHandler: () => {
        dispatch(openEdifySpinner(true));
        newProjectService
          .deleteProject(id)
          .then(() => {
            (dispatch as any)(getProjectsByType("PRIVATE"));
            dispatch(closeMainModal());
            navigate("/obrasprivadas/explorar");
            dispatch(openEdifySpinner(false));
            fireAnalytics("project_deleted", {
              edify_user_id: userData?.id ?? "",
              session_id: getSessionId(),
              device_category: getDeviceCategory(),
              category: "publishing",
              action: "project_deleted",
              project_type: "PRIVATE",
              project_id: String(id)
            });
            dispatch(openMainModal({
              title: " ¡Eliminado! ",
              body: "Tu proyecto se ha eliminado con éxito",
              iconType: "success",
              isSuccess: true,
              confirmHandler: null,
            }));
          })
          .catch((err) => {
            console.error(err);
            dispatch(openEdifySpinner(false));
            dispatch(openMainModal({
              title: "Ups!",
              body: "Se produjo un error al eliminar el proyecto, intente nuevamente o escribanos a contacto@edify.la ¡Muchas gracias!",
              isSuccess: true,
              confirmHandler: null,
              iconType: "error",
            }));
          });
      },
    }));
  }, [dispatch, navigate, userData?.id]);

  const generateTempSubProjectId = useCallback((): string => {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Preparar subproyecto para creación
  const prepareSubProjectForCreation = useCallback((subProject: NewSubProject): NewSubProject => {
    return {
      ...subProject,
      id: subProject.id || generateTempSubProjectId(),
      isNew: true,
      isEdited: false
    };
  }, [generateTempSubProjectId]);

  // Preparar proyecto para creación
  const prepareProjectForCreation = useCallback((projectData: NewProjectFormData): NewProjectFormData => {
    return {
      ...projectData,
      // Asegurar que las fechas estén en el formato correcto
      startDate: projectData.startDate || '',
      endDate: projectData.endDate || ''
    };
  }, []);

  // Verificar si se puede publicar
  const canPublishProject = useCallback((
    projectData: NewProjectFormData,
    subProjects: NewSubProject[],
    images: NewProjectImage[]
  ): boolean => {
    return validateProjectForCreation(projectData) && 
           validateSubProjectsForCreation(subProjects) && 
           validateImagesForCreation(images);
  }, [validateProjectForCreation, validateSubProjectsForCreation, validateImagesForCreation]);

  // Obtener errores de validación para creación
  const getCreationValidationErrors = useCallback((
    projectData: NewProjectFormData,
    subProjects: NewSubProject[],
    images: NewProjectImage[]
  ) => {
    const errors: string[] = [];

    if (!validateProjectForCreation(projectData)) {
      errors.push('Los datos del proyecto no son válidos');
    }

    if (!validateSubProjectsForCreation(subProjects)) {
      errors.push('Debe agregar al menos un subproyecto válido');
    }

    if (!validateImagesForCreation(images)) {
      errors.push('Debe agregar al menos una imagen');
    }

    return errors;
  }, [validateProjectForCreation, validateSubProjectsForCreation, validateImagesForCreation]);

  // Configuración específica para creación
  const creationConfig = useMemo(() => ({
    maxSubProjects: 10,
    minSubProjects: 1,
    maxImages: 5,
    minImages: 1,
    allowedImageTypes: ['image/jpeg', 'image/png', 'image/webp'],
    maxImageSize: 5 * 1024 * 1024, // 5MB
    dateFormat: 'YYYY-MM-DD',
    timezone: 'America/Argentina/Buenos_Aires'
  }), []);

  return {
    // Validaciones
    validateProjectForCreation,
    validateSubProjectsForCreation,
    validateImagesForCreation,
    canPublishProject,
    getCreationValidationErrors,
    
    // Operaciones
    createProjectWithSubProjects,
    deleteProjectWithFlow,
    
    // Utilidades
    generateTempSubProjectId,
    prepareSubProjectForCreation,
    prepareProjectForCreation,
    
    // Configuración
    creationConfig
  };
};
